import { DEFAULT_LABELS } from './labels';

export type LabelsType = typeof DEFAULT_LABELS;

export type Steps = 0 | 1 | 2 | 3 | 4;

export type BrandedLoanCalculatorProps = {
    labels: LabelsType;
    fixedRate: number;
    loanAmountMin: number;
    loanAmountMax: number;
    loanAmountDefault: number;
    tenorYearsMin: number;
    tenorYearsMax: number;
    tenorYearsDefault: number;
    productType: string;
    partner: string;
    loanType?: LoanType;
    secciUrl: {
        url: string;
        title: string;
    };
    commonTermsUrl: {
        url: string;
        title: string;
    };
    insuranceLinks?: {
        url: string;
        title: string;
    }[];
    showTestApplyLinks?: boolean;
    brandColor: string;
};

export type CalculatorStateType = {
    loanAmount: number;
    tenorYears: number;
    showGovIdField: boolean;
};

export type HookProps = {
    fixedRate: number;
    loanAmountDefault: number;
    tenorYearsDefault: number;
    partner: string;
    productType: string;
};

type GetLabel = (key: keyof LabelsType, replacements?: Record<string, string | number>) => string;

export type InputProps = {
    label: string;
    suffix: string;
    min: number;
    max: number;
    value: number | null;
    onChange: (value: number | null) => void;
};

export type SliderProps = {
    handleSize?: number;
    min: number;
    max: number;
    value: number | null;
    step?: number;
    onChange: (value: number) => void;
    label: string;
    brandColor: string;
};

export type DescriptionProps = {
    title: string;
    text: string;
    commonTermsTitle: string;
    commonTermsUrl: string | null;
    secciTitle: string;
    secciUrl: string | null;
    brandColor?: string;
};

export type StepCommonProps = {
    step: number;
    setStep: (step: Steps) => void;
    setValues: (values: FieldValuesType) => void;
    fields: FieldValuesType;
    getValuesFromStorage: () => FieldValuesType | null;
    updateFields: (value: Partial<FieldValuesType>) => void;
    getLabel: GetLabel;
    showTestApplyLinks: boolean;
    brandColor: string;
    scrollToTop: () => void;
};

export type FormProps = StepCommonProps & {
    updateState: (key: string, value: number | boolean) => void;
    loanAmountMin: number;
    loanAmountMax: number;
    tenorYearsMin: number;
    tenorYearsMax: number;
    calculatorState: CalculatorStateType;
    monthlyAmount: number | null;
    formatNumber: (number: number) => string;
    login: () => void;
    partner?: string;
};

export type StepTwoProps = StepCommonProps & {
    insuranceLinks: BrandedLoanCalculatorProps['insuranceLinks'];
};

export type LoanType =
    | 'MOTOR'
    | 'WEDDING'
    | 'BOAT'
    | 'INVESTMENT'
    | 'DRIVERS_LICENSE'
    | 'INTERIOR'
    | 'RENOVATION'
    | 'TRAVEL'
    | 'CONSOLIDATE_LOANS'
    | 'DENTAL_OR_OTHER_CARE'
    | 'STUDIES'
    | 'OTHER'
    | 'HOUSING_MORTGAGE_LOAN';
export type EmploymentType = 'FAST_ANSTALLD' | 'VIKARIAT' | 'EGEN_FORETAGARE' | 'PENSIONAR' | 'ARBETSLOS';
export type MaritalStatus = 'SINGLE' | 'MARRIED' | 'DOMESTIC_PARTNER';
export type HabitationForm = 'APARTMENT' | 'CONDOMINIUM' | 'DETACHED_HOUSE';

export type FieldValuesType = {
    governmentId: string;
    phoneMobile: string;
    contactEmail: string;
    purposeOfLoan: LoanType;
    applicantMaritalStatus: string;
    numberChildrenNotOfAge: string;
    applicantHabitationForm: string;
    employmentTypeQualifying: '' | 'true' | 'false';
    applicantEmploymentType?: EmploymentType;
    applicantEmployer: string;
    applicantEmploymentYearFrom: string;
    applicantEmploymentMonthFrom: string;
    applicantEmploymentYearUntil: string;
    applicantEmploymentMonthUntil: string;
    montlyIncomeGross: string;
    livingExpenses: string;
    otherLoanExpenses: string;
    insurance: boolean;
    agreementSignType: string;
    payoutAccount: {
        clearingNumber: string;
        accountNumber: string;
    };
    approveConditions: boolean;
};

export type AccountValues = {
    clearingNumber: string;
    accountNumber: string;
};

export type AccountFieldProps = {
    values: AccountValues;
    setValues: (values: Partial<AccountValues>) => void;
    onValidChange?: (valid: boolean) => void;
    getLabel: GetLabel;
    showValidationErrors?: boolean;
};

export type EmploymentNeed = {
    applicantEmployer?: boolean;
    applicantEmploymentYearFrom?: boolean;
    applicantEmploymentMonthFrom?: boolean;
    applicantEmploymentYearUntil?: boolean;
    applicantEmploymentMonthUntil?: boolean;
    employmentTypeQualifying?: boolean;
};

export type DecisionResultType = {
    applicationReference: string;
    decision: ApplicationResponseType;
    approvedAmount: number;
    interest: number;
    effectiveInterest: number;
    tenor: number;
    monthlyCost: number;
    totalRepaymentAmount: number;
    totalFeesAndInterest: number;
    adminFee: number;
    arrangementFee: number;
    consolidationDemand: number;
};

export enum ApplicationResponseType {
    APPROVED = 'APPROVED',
    REJECTED = 'REJECTED',
    MANUAL_INSPECTION = 'MANUAL_INSPECTION',
}

export type SubmitApplicationOk = {
    status: 'ok';
    result: {
        submitApplicationExtResult: {
            applicationReference: string;
            decision: ApplicationResponseType;
            approvedAmount: number;
            interest: number;
            tenor: number;
            effectiveInterest: number;
            monthlyCost: number;
            consolidationDemand: number;
            adminFee: number;
            arrangementFee: number;
            documentTypes: string[];
            totalRepaymentAmount: number;
            monthlyAccountFee: number;
            totalFeesAndInterest: number;
        };
    };
};

export type SubmitApplicationError = {
    status: 'error';
    type: 'ValidationError' | 'ApplicationError'; // extend as needed
    message: string;
};

export type SubmitApplicationResponse = SubmitApplicationOk | SubmitApplicationError;

export type ApplicationAcceptedOk = {
    status: 'ok';
    result: {
        acceptQuoteResult: {
            accepted: true;
            message: 'Offer accepted';
        };
    };
};

export const applicationResponseTypeDict = {
    [ApplicationResponseType.APPROVED]: 'Approved',
    [ApplicationResponseType.REJECTED]: 'Rejected',
    [ApplicationResponseType.MANUAL_INSPECTION]: 'Manual Inspection',
};
